import { QUALITY_MAX_AGE_DAYS, type StoreGrade } from '../audit/grades';
import { districtBreakdown, storeBreakdown, type DistrictBreakdown, type StoreBreakdown } from '../audit/gradeBreakdown';
import { computeWindowStatus, daysOverdue, WINDOW_URGENCY, type WindowStatus } from '../audit/windows';
import { windowDays, type Party } from '../audit/requirements';
import { appliesTo } from '../audit/assignment';
import type { AuditRecord } from './audits';
import type { StoreStatusRow } from './storeStatus';
import { isOverdue, type ServerTask } from './tasks';
import { isUntouchedOverdue } from './taskActivity';
import type { PublishedTemplate } from './templates';
import type { Store } from './types';

/**
 * Dashboard rows (Phase 6a), computed from data the app already reads:
 * StoreAuditStatus dates + template windows (status LIVE, never the stored
 * WindowStatus), open tasks, and submitted audits. Pure — the home page and
 * district pages share it, and it is unit-tested.
 */

export interface StoreRow {
  store: Store;
  /**
   * Worst window across every published template's responsible-party
   * clocks. A clock not started yet ('never') only counts when NO clock has
   * started — a store current on its DM clock isn't "Never audited" just
   * because the Auditor hasn't been yet (no nagging before a first audit).
   */
  status: WindowStatus;
  template: PublishedTemplate | null;
  /** Whose clock `status` is (null when nothing applies). */
  party: Party | null;
  nextDue: string | null;
  lastCompleted: string | null;
  /**
   * Every STARTED clock, worst first (DM persona, reflect 2026-10-25: the
   * row named one audit and hid the other). The first is the row's own
   * status/template/party/dates. Empty while no clock has started.
   */
  clocks: BoardClock[];
  /** The live templates assigned to this store (the Long-distance tag's windows). */
  applies: PublishedTemplate[];
  openTasks: number;
  overdueTasks: number;
  /** Overdue tasks nobody has touched since they fell overdue (src/data/taskActivity.ts). */
  untouchedOverdueTasks: number;
  /** Timeliness + quality (owner ruling 2026-10-19: two grades, not one). */
  grade: StoreGrade;
  /** "Why this grade?" — every clock and open task behind timeliness, the audits behind quality. */
  breakdown: GradeBreakdown;
}

/** One started clock on a board row. */
export interface BoardClock {
  status: WindowStatus;
  template: PublishedTemplate;
  party: Party;
  nextDue: string | null;
  lastCompleted: string | null;
}

/** Worst status first; the same status → the clock due first (the most overdue). */
export function byClockUrgency(a: BoardClock, b: BoardClock): number {
  return WINDOW_URGENCY[a.status] - WINDOW_URGENCY[b.status] || (a.nextDue ?? '9999').localeCompare(b.nextDue ?? '9999');
}

/** What a clock fact carries for the popover. */
export interface ClockRef {
  template: PublishedTemplate;
  nextDue: string | null;
  lastCompleted: string | null;
}
export type GradeBreakdown = StoreBreakdown<ClockRef, ServerTask, AuditRecord>;

export interface DistrictRow {
  district: string;
  stores: number;
  /** Average timeliness of the district's audited stores; null = none audited yet. */
  timeliness: number | null;
  /** Average quality of the stores with a scored audit; null = none yet. */
  quality: number | null;
  /** Stores with an overdue audit. */
  behind: number;
  /** Stores not audited yet — not "behind": the clock starts at the first audit (owner ruling 2026-10-16). */
  neverAudited: number;
  openTasks: number;
  overdueTasks: number;
  /** Overdue tasks with no activity since they fell overdue — "is anyone on it?" (owner ruling 2026-10-21). */
  untouchedOverdueTasks: number;
  /** "Why this grade?" — per grade: causes averaged over graded stores + which stores pull it down. */
  breakdown: DistrictBreakdown<StoreRow>;
}

export function buildStoreRows(input: {
  stores: readonly Store[];
  statuses: readonly StoreStatusRow[];
  templates: readonly PublishedTemplate[];
  openTasks: readonly ServerTask[];
  submittedAudits: readonly AuditRecord[];
  now?: Date;
}): StoreRow[] {
  const now = input.now ?? new Date();
  // Newest first per store, last ~13 months only (an older audit never sets
  // today's Quality — owner ruling 2026-10-19). A store team's self-audit
  // never counts (its lateness counts, its own score doesn't). A date in
  // the future (a wrong device clock, or written on purpose) doesn't count
  // either — it would otherwise stay "newest" and hold a Quality slot for
  // years. An hour's slack covers a slightly fast clock.
  const cutoff = new Date(now.getTime() - QUALITY_MAX_AGE_DAYS * 86_400_000).toISOString();
  const latest = new Date(now.getTime() + 3_600_000).toISOString();
  const auditsByStore = new Map<number, AuditRecord[]>();
  for (const a of [...input.submittedAudits].sort((x, y) => y.submittedAt.localeCompare(x.submittedAt))) {
    if (a.party === 'store' || !a.submittedAt || a.submittedAt < cutoff || a.submittedAt > latest) continue;
    const list = auditsByStore.get(a.storeId) ?? [];
    list.push(a);
    auditsByStore.set(a.storeId, list);
  }

  const inScope = new Set(input.stores.map((st) => Number(st.id)));
  return input.stores.map((store) => {
    const storeId = Number(store.id);
    type Clock = Pick<StoreRow, 'status' | 'template' | 'party' | 'nextDue' | 'lastCompleted'>;
    const clocks: { status: WindowStatus; daysOverdue: number; party: Party; ref: ClockRef }[] = [];
    // Only templates assigned to this store (src/audit/assignment.ts).
    const applies = input.templates.filter((t) => appliesTo(t.assignment, store));
    for (const template of applies) {
      for (const req of template.requirements) {
        const row = input.statuses.find(
          (r) => r.storeId === storeId && r.templateId === template.id && r.party === req.party,
        );
        const status = computeWindowStatus(
          {
            lastCompleted: row?.lastCompletedDate ?? null,
            nextDue: row?.nextDueDate ?? null,
            frequencyDays: windowDays(req, store.longDistance),
            dueSoonThresholdPct: template.dueSoonThresholdPct,
          },
          now,
        );
        clocks.push({
          status,
          daysOverdue: daysOverdue(row?.nextDueDate ?? null, now),
          party: req.party,
          ref: { template, nextDue: row?.nextDueDate ?? null, lastCompleted: row?.lastCompletedDate ?? null },
        });
      }
    }
    // A store counts as audited (and gets a grade) only once a DM or Auditor
    // clock has started — a store team's own self-audit never turns a
    // never-audited store "current" or gives it a grade (QA review
    // 2026-10-19). Until then its store-team clocks are left out.
    const staffStarted = clocks.some((c) => c.party !== 'store' && c.status !== 'never');
    const counted = staffStarted ? clocks : clocks.filter((c) => c.party !== 'store');
    const started: BoardClock[] = counted
      .filter((c) => c.status !== 'never')
      .map((c) => ({
        status: c.status,
        template: c.ref.template,
        party: c.party,
        nextDue: c.ref.nextDue,
        lastCompleted: c.ref.lastCompleted,
      }))
      // Same status → the clock due first (the most overdue) names the row;
      // round 5: the board said "17d — DM Facility" while DM Visit was 33d late.
      .sort(byClockUrgency);
    const first = counted[0];
    const worst: Clock = started[0] ?? {
      status: 'never',
      template: first?.ref.template ?? null,
      party: first?.party ?? null,
      nextDue: first?.ref.nextDue ?? null,
      lastCompleted: first?.ref.lastCompleted ?? null,
    };
    // A task belongs to its own store. Assigned to this store's account but
    // filed against a store NOT on this board, it counts here instead — never
    // on two rows at once (that double-counted districts and grades).
    const manager = store.managerEmail.trim().toLowerCase();
    const storeTasks = input.openTasks.filter(
      (t) =>
        t.storeId === storeId ||
        (!inScope.has(t.storeId) && manager !== '' && t.assignedTo.trim().toLowerCase() === manager),
    );
    const audits = auditsByStore.get(storeId) ?? [];
    const breakdown = storeBreakdown({
      clocks: counted,
      tasks: storeTasks.map((t) => ({ overdue: isOverdue(t, now), ref: t })),
      audits: audits.map((a) => ({ score: a.score, ref: a })),
    });
    return {
      store,
      ...worst,
      clocks: started,
      applies,
      openTasks: storeTasks.length,
      overdueTasks: storeTasks.filter((t) => isOverdue(t, now)).length,
      untouchedOverdueTasks: storeTasks.filter((t) => isUntouchedOverdue(t, now)).length,
      grade: breakdown.grade,
      breakdown,
    };
  });
}

/**
 * The soonest due date on a row ("Sort: next due") — every clock is on the
 * row now, so sorting by the worst clock's date put a store due in 6 days
 * after one due in 10 (QA 2026-10-25).
 */
export function earliestDue(row: Pick<StoreRow, 'clocks' | 'nextDue'>): string | null {
  const dates = row.clocks.map((c) => c.nextDue).filter((d): d is string => !!d);
  return dates.length > 0 ? dates.reduce((a, b) => (b < a ? b : a)) : row.nextDue;
}

/**
 * The board's default order ("Sort: most urgent") — worst audit status,
 * then soonest due, then store number. Home AND district pages use it, so
 * the same stores come first on both (exec round 2 F8).
 */
export function byUrgency(a: StoreRow, b: StoreRow): number {
  return (
    WINDOW_URGENCY[a.status] - WINDOW_URGENCY[b.status] ||
    (a.nextDue ?? '9999').localeCompare(b.nextDue ?? '9999') ||
    a.store.storeNumber.localeCompare(b.store.storeNumber, undefined, { numeric: true })
  );
}

/**
 * "8 of 13 stores overdue" (owner ruling 2026-10-22, round 3: a district can
 * average green while most of its stores are late — say it beside the
 * grade). `behind` = stores with an overdue audit (DistrictRow.behind; a
 * never-audited store is never overdue). null when none is.
 */
export function overdueStoresText(behind: number, stores: number): string | null {
  if (behind <= 0) return null;
  return `${behind} of ${stores} ${stores === 1 ? 'store' : 'stores'} overdue`;
}

/** District roll-up for the executive view, sorted worst timeliness first, then worst quality (ungraded last). */
export function buildDistrictRows(rows: readonly StoreRow[]): DistrictRow[] {
  // Trimmed + case-insensitive, like every district comparison (scope.ts);
  // the first spelling seen is the one shown.
  const byDistrict = new Map<string, { district: string; list: StoreRow[] }>();
  for (const r of rows) {
    const district = r.store.district.trim();
    const key = district.toLowerCase();
    const entry = byDistrict.get(key) ?? { district, list: [] };
    entry.list.push(r);
    byDistrict.set(key, entry);
  }
  return [...byDistrict.values()]
    .map(({ district, list }) => {
      const breakdown = districtBreakdown(list.map((r) => ({ store: r, breakdown: r.breakdown })));
      return {
        district,
        stores: list.length,
        timeliness: breakdown.timeliness.grade,
        quality: breakdown.quality.grade,
        behind: list.filter((r) => r.status === 'overdue').length,
        neverAudited: list.filter((r) => r.status === 'never').length,
        openTasks: list.reduce((n, r) => n + r.openTasks, 0),
        overdueTasks: list.reduce((n, r) => n + r.overdueTasks, 0),
        untouchedOverdueTasks: list.reduce((n, r) => n + r.untouchedOverdueTasks, 0),
        breakdown,
      };
    })
    .sort(
      (a, b) =>
        (a.timeliness ?? Infinity) - (b.timeliness ?? Infinity) ||
        (a.quality ?? Infinity) - (b.quality ?? Infinity) ||
        (a.district || '\uffff').localeCompare(b.district || '\uffff'),
    );
}
